'use client'

import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { authFetchJson } from '@/shared/lib/auth-fetch'
import {
  useVolunteerTableRowsForGroup,
  useRemoveGroupVolunteer,
  useVolunteerOrganizationNames,
} from '@/features/volunteers/hooks/volunteers.hooks'
import type { Group } from '@/features/stm/lib/types'
import type { VolunteerTableRow } from '@/features/volunteers/types/volunteers.type'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/shared/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Filter,
  Search,
  DownloadCloud,
  Loader2,
  Pencil,
  Trash2,
  HardHat,
  Sparkles,
  UserCheck,
  ChevronDown,
  ChefHat,
  Mail,
  UtensilsCrossed,
  Users,
  Printer,
} from 'lucide-react'
import { toast } from 'sonner'
import { RolesFilterContent } from '@/features/volunteers/components/column-filter-contents'
import { roleFilterOptions } from '@/features/volunteers/constants/options'
import { cn, formatPhoneNumber, shortenName } from '@/shared/lib/utils'
import { maskHomeownerLastName, maskHomeownerPhone } from '@/shared/lib/utils/homeowner-privacy.utils'
import { useIsMobile } from '@/shared/hooks/use-mobile'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'

/** Row data used for column filtering (derived from volunteer table row). */
type UserRow = {
  user: VolunteerTableRow
  volunteerName: string
  org: string
  role: string
  arrivalStr: string
  departureStr: string
  addedStr: string
  regionId: string
  lastActiveStr: string
}

type SkillColumn = {
  key: string
  label: string
  field: string
  category: 'construction' | 'other' | 'cooking'
  headerClassName?: string
  cellClassName?: string
}

type HydratedVolunteerMeta = {
  note: string
  flightDetails: string
  dateOfBirth: string | Date | null
}

const SKILL_COLUMNS: SkillColumn[] = [
  { key: 'team-leader', label: 'Team Leader', field: 'experienceLeadingTeams', category: 'construction' },
  { key: 'manual-labor', label: 'Manual Labor', field: 'experienceManualLabor', category: 'construction' },
  { key: 'general-construction', label: 'Gen Const', field: 'experienceConstruction', category: 'construction' },
  { key: 'drywall', label: 'Drywall Finishing', field: 'experienceDrywallFinishing', category: 'construction' },
  { key: 'rough-carpentry', label: 'Rough Carpentry', field: 'experienceRoughCarpentry', category: 'construction' },
  { key: 'finish-carpentry', label: 'Finish Carpentry', field: 'experienceFinishCarpentry', category: 'construction' },
  { key: 'flooring', label: 'Flooring', field: 'experienceFlooring', category: 'construction' },
  { key: 'woodworking', label: 'Woodworking', field: 'experienceWoodWorking', category: 'construction' },
  { key: 'electrical', label: 'Electrical', field: 'experienceElectrical', category: 'construction' },
  { key: 'plumbing', label: 'Plumbing', field: 'experiencePlumbing', category: 'construction' },
  { key: 'hvac', label: 'HVAC', field: 'experienceHvac', category: 'construction' },
  { key: 'arborist', label: 'Tree Work', field: 'experienceArborist', category: 'construction' },
  { key: 'roofing', label: 'Roofing', field: 'experienceRoofing', category: 'construction' },
  { key: 'heavy-equipment', label: 'Heavy Equipment', field: 'experienceHeavyEquipment', category: 'construction' },
  { key: 'alt-ministries', label: 'Alt Ministries', field: 'experienceAltMinistries', category: 'other' },
  { key: 'data-management', label: 'Data Mgmt', field: 'experienceDataManagement', category: 'other' },
  { key: 'storytelling', label: 'Social Media', field: 'experienceStorytelling', category: 'other', headerClassName: 'w-[104px]', cellClassName: 'w-[104px]' },
  { key: 'cooking', label: 'Cooking', field: 'mealPreparationAbility', category: 'cooking' },
]

const DEFAULT_PRINT_COLUMNS = new Set([
  'volunteer',
  'srUserId',
  'age',
  'mf',
  'role',
  'general-construction',
  'alt-ministries',
  'cooking',
  'notes',
  'flightDetails',
])
const ALL_SKILLS_FILTER_OPTIONS = SKILL_COLUMNS.map((column) => ({ value: column.field, label: column.label }))
const RATING_FILTER_OPTIONS = [1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: String(n) }))
const AGE_FILTER_OPTIONS = [
  { value: 'under-18', label: 'Under 18' },
  { value: '18-24', label: '18-24' },
  { value: '25-34', label: '25-34' },
  { value: '35-44', label: '35-44' },
  { value: '45-54', label: '45-54' },
  { value: '55-plus', label: '55+' },
] as const
const GENDER_FILTER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
]

/** Get 1–5 experience/skill rating from user or user.volunteer. */
function getExperienceRating(u: VolunteerTableRow | NonHomeownerUser | null, skillKey: string): number | null {
  if (!u) return null
  const uAny = u as Record<string, unknown>
  const raw = uAny[skillKey] ?? (uAny.volunteer as Record<string, unknown> | undefined)?.[skillKey]
  return typeof raw === 'number' ? raw : null
}

function getSkillColumnRating(user: VolunteerTableRow, column: SkillColumn): number | null {
  const u = user as Record<string, unknown>
  const raw = u[column.field] ?? (u.volunteer as Record<string, unknown> | undefined)?.[column.field]
  if (typeof raw === 'number') {
    return raw >= 1 && raw <= 5 ? raw : null
  }
  if (typeof raw === 'string') {
    const n = parseInt(raw.trim(), 10)
    return !Number.isNaN(n) && n >= 1 && n <= 5 ? n : null
  }
  return null
}

function renderCompactHeaderLabel(label: string): React.ReactNode {
  const words = label.trim().split(/\s+/)
  if (words.length === 2) {
    return (
      <span className="inline-flex flex-col leading-tight text-center">
        <span>{words[0]}</span>
        <span>{words[1]}</span>
      </span>
    )
  }
  return <span>{label}</span>
}

function FilterDropdownField({
  title,
  value,
  onApply,
  options,
}: {
  title: string
  value: string[]
  onApply: (v: string[] | null) => void
  options: readonly { value: string; label: string }[]
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">{title}</p>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="w-full justify-between h-9 border-input text-foreground hover:bg-accent/50"
            data-avoid-dialog-close
          >
            <span className="truncate">{value.length > 0 ? `${value.length} selected` : 'Select'}</span>
            <ChevronDown className={cn('h-4 w-4 text-muted-foreground', value.length > 0 && 'text-primary')} />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0" data-avoid-dialog-close>
          <RolesFilterContent value={value} onApply={onApply} options={options} />
        </PopoverContent>
      </Popover>
    </div>
  )
}
import { format } from 'date-fns'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { Checkbox } from '@/shared/components/ui/checkbox'
import AddVolunteerButton from '@/features/volunteers/components/add-volunteer-button'
import FormDialog from '@/shared/components/dialog/form-dialog'
import UpdateUserForm from '@/features/volunteers/forms/user-form/update-user-form'
import { getNonHomeownerUserById } from '@/features/volunteers/api/volunteers.api'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useRegion } from '@/shared/hooks/use-region'
import type { NonHomeownerUser } from '@/features/volunteers/types/volunteers.type'

type GroupParticipantsTabProps = {
  group: Group
  isVolunteerView?: boolean
}

function isGroupLeaderUser(u: VolunteerTableRow, group: Group): boolean {
  if (group.contactUserId != null && u.id === group.contactUserId) return true
  const email = (u.email ?? (u as { user?: { email?: string } }).user?.email ?? '').trim()
  const emailMatch =
    group.contact_email?.trim() &&
    email &&
    group.contact_email.trim().toLowerCase() === email.toLowerCase()
  const nameMatch =
    group.contact_name?.trim() &&
    (u.fullname ?? '').trim() &&
    group.contact_name.trim().toLowerCase() === (u.fullname ?? '').trim().toLowerCase()
  return !!(emailMatch || nameMatch)
}

function formatSrMoney(v: string | null | undefined): string {
  if (v == null || String(v).trim() === '') return '—'
  const n = Number(v)
  if (Number.isNaN(n)) return String(v)
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

/** Numeric compare for fundraising columns (groupPrice / groupPaid). */
function parseMoneySortValue(v: string | null | undefined): number {
  if (v == null || String(v).trim() === '') return Number.NEGATIVE_INFINITY
  const n = Number(v)
  return Number.isNaN(n) ? Number.NEGATIVE_INFINITY : n
}

function compareUserRowsForSort(
  a: UserRow,
  b: UserRow,
  sortColumn: string,
  getVolunteerAge: (u: VolunteerTableRow) => number | null,
  getVolunteerNotes: (u: VolunteerTableRow) => string,
  getVolunteerFlightDetails: (u: VolunteerTableRow) => string,
  singleSkillField: string | null
): number {
  switch (sortColumn) {
    case 'volunteer':
      return a.volunteerName.localeCompare(b.volunteerName, undefined, { sensitivity: 'base' })
    case 'srUserId': {
      const aId = a.user.volunteer?.srUserId
      const bId = b.user.volunteer?.srUserId
      if (aId == null && bId == null) return 0
      if (aId == null) return 1
      if (bId == null) return -1
      return aId - bId
    }
    case 'role':
      return a.role.localeCompare(b.role)
    case 'experience-skill':
      if (!singleSkillField) return 0
      return (
        (getExperienceRating(a.user, singleSkillField) ?? -1) -
        (getExperienceRating(b.user, singleSkillField) ?? -1)
      )
    default: {
      const skillCol = SKILL_COLUMNS.find((c) => c.key === sortColumn)
      if (skillCol) {
        return (
          (getSkillColumnRating(a.user, skillCol) ?? -1) -
          (getSkillColumnRating(b.user, skillCol) ?? -1)
        )
      }
      if (sortColumn === 'mf') {
        return volunteerGenderShort(a.user).localeCompare(volunteerGenderShort(b.user), undefined, {
          sensitivity: 'base',
        })
      }
      if (sortColumn === 'age') {
        const ageA = getVolunteerAge(a.user)
        const ageB = getVolunteerAge(b.user)
        return (ageA ?? -1) - (ageB ?? -1)
      }
      if (sortColumn === 'notes') {
        return getVolunteerNotes(a.user).localeCompare(getVolunteerNotes(b.user), undefined, {
          sensitivity: 'base',
        })
      }
      if (sortColumn === 'flightDetails') {
        return getVolunteerFlightDetails(a.user).localeCompare(getVolunteerFlightDetails(b.user), undefined, {
          sensitivity: 'base',
        })
      }
      if (sortColumn === 'lastActive') return a.lastActiveStr.localeCompare(b.lastActiveStr)
      if (sortColumn === 'added') return a.addedStr.localeCompare(b.addedStr)
      if (sortColumn === 'goal')
        return parseMoneySortValue(a.user.groupPrice) - parseMoneySortValue(b.user.groupPrice)
      if (sortColumn === 'raised')
        return parseMoneySortValue(a.user.groupPaid) - parseMoneySortValue(b.user.groupPaid)
      return 0
    }
  }
}

type ColumnSortButtonProps = {
  columnKey: string
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  /** Cycles: default → descending → ascending → default. */
  onCycleSort: (columnKey: string) => void
  ariaLabelBase: string
}

function ColumnSortButton({
  columnKey,
  sortColumn,
  sortDirection,
  onCycleSort,
  ariaLabelBase,
}: ColumnSortButtonProps) {
  const active = sortColumn === columnKey
  const mode = !active ? 'none' : sortDirection === 'asc' ? 'asc' : 'desc'
  const ariaLabel =
    mode === 'none'
      ? `${ariaLabelBase}: sort descending`
      : mode === 'desc'
        ? `${ariaLabelBase}: sort ascending`
        : `${ariaLabelBase}: clear sort order`
  const Icon = mode === 'asc' ? ArrowUp : mode === 'desc' ? ArrowDown : ArrowUpDown
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0 p-0 print:hidden"
      aria-label={ariaLabel}
      onClick={() => onCycleSort(columnKey)}
    >
      <Icon className={cn('h-3.5 w-3.5', active ? 'text-primary' : 'text-muted-foreground')} />
    </Button>
  )
}

function parseDateValue(dateValue: string | Date | undefined | null): Date | null {
  if (!dateValue) return null
  if (dateValue instanceof Date) return isNaN(dateValue.getTime()) ? null : dateValue
  if (typeof dateValue === 'string') {
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      const [y, m, d] = dateValue.split('-').map(Number)
      const date = new Date(y, m - 1, d)
      return isNaN(date.getTime()) ? null : date
    }
    const date = new Date(dateValue)
    return isNaN(date.getTime()) ? null : date
  }
  return null
}

function getAgeFromDateOfBirth(dateOfBirth: string | Date | undefined | null): number | null {
  const dob = parseDateValue(dateOfBirth)
  if (!dob) return null
  const today = new Date()
  let age = today.getFullYear() - dob.getFullYear()
  const monthDiff = today.getMonth() - dob.getMonth()
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1
  }
  return age >= 0 && age <= 120 ? age : null
}

/** Single-letter display from user or volunteer gender (table-rows API). */
function volunteerGenderShort(u: VolunteerTableRow): string {
  const g = (u.gender ?? u.volunteer?.gender ?? '').toString().trim().toLowerCase()
  if (g === 'male' || g === 'm') return 'M'
  if (g === 'female' || g === 'f') return 'F'
  return '—'
}

function matchesAgeBucket(age: number, bucket: string): boolean {
  if (bucket === 'under-18') return age < 18
  if (bucket === '18-24') return age >= 18 && age <= 24
  if (bucket === '25-34') return age >= 25 && age <= 34
  if (bucket === '35-44') return age >= 35 && age <= 44
  if (bucket === '45-54') return age >= 45 && age <= 54
  if (bucket === '55-plus') return age >= 55
  return false
}

function findRelevantServePeriod(serveDates: Array<{ from: string; to: string }> | null | undefined): { from: Date | null; to: Date | null } {
  if (!serveDates?.length) return { from: null, to: null }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const parsed = serveDates
    .map((period) => {
      const from = parseDateValue(period.from)
      const to = parseDateValue(period.to)
      if (!from || !to) return null
      return { from, to }
    })
    .filter((p): p is { from: Date; to: Date } => p !== null)
    .sort((a, b) => a.from.getTime() - b.from.getTime())
  if (parsed.length === 0) return { from: null, to: null }
  const current = parsed.find((p) => today >= p.from && today <= p.to)
  if (current) return current
  const upcoming = parsed.find((p) => p.from > today)
  if (upcoming) return upcoming
  const last = parsed[parsed.length - 1]
  return last
}

/** Build row from volunteer table row. */
function buildUserRow(u: VolunteerTableRow): UserRow {
  const volunteerData = u.volunteer
  const servePeriod = findRelevantServePeriod(volunteerData?.serveDates ?? null)
  const org = (u.volunteer?.organizationName ?? '').trim()
  const role = (u.role as { name?: string } | undefined)?.name ?? ''
  const regionId = volunteerData?.region?.id != null ? String(volunteerData.region.id) : ''
  const lastActiveAt = u.lastActiveAt
  const lastActiveStr = lastActiveAt
    ? format(parseDateValue(lastActiveAt) ?? new Date(), 'yyyy-MM-dd')
    : ''
  const volunteerName = (u.fullname ?? '').trim()
  return {
    user: u,
    volunteerName,
    org,
    role,
    arrivalStr: servePeriod.from ? format(servePeriod.from, 'yyyy-MM-dd') : '',
    departureStr: servePeriod.to ? format(servePeriod.to, 'yyyy-MM-dd') : '',
    addedStr: u.joinedAt ? format(new Date(u.joinedAt), 'yyyy-MM-dd') : '',
    regionId,
    lastActiveStr,
  }
}

function SkillsBannerCard({
  title,
  count,
  countNode,
  icon,
  highlight,
  users,
  sections,
  onSelectUser,
  displayNameForDropdown,
  dropdownClickable = true,
  hideDropdown = false,
}: {
  title: string
  count: number
  countNode?: React.ReactNode
  icon: React.ReactNode
  highlight?: 'construction' | 'other' | 'info' | 'neutral'
  users: VolunteerTableRow[]
  /** When set, dropdown shows section labels (e.g. Male / Female) with grouped names. */
  sections?: Array<{ label: string; users: VolunteerTableRow[] }>
  onSelectUser: (user: VolunteerTableRow) => void
  /** Optional: override how name is displayed in dropdown (e.g. masked for privacy). */
  displayNameForDropdown?: (u: VolunteerTableRow) => string
  /** When false, dropdown names are read-only (not clickable). */
  dropdownClickable?: boolean
  /** When true, card is read-only and does not render a names dropdown. */
  hideDropdown?: boolean
}) {
  const isMobile = useIsMobile()
  const displayName = displayNameForDropdown ?? ((u: VolunteerTableRow) => (u.fullname ?? 'Unknown'))
  const cardHighlightClass =
    highlight === 'construction'
      ? 'bg-emerald-50/70 border-emerald-200/70 dark:bg-emerald-950/25 dark:border-emerald-900/50'
      : highlight === 'other'
        ? 'bg-amber-50/70 border-amber-200/70 dark:bg-amber-950/25 dark:border-amber-900/50'
        : highlight === 'info'
          ? 'bg-sky-50/70 border-sky-200/70 dark:bg-sky-950/25 dark:border-sky-900/50'
          : highlight === 'neutral'
            ? 'bg-slate-50/70 border-slate-200/70 dark:bg-slate-900/25 dark:border-slate-800/60'
            : 'bg-background border-border'
  const iconHighlightClass =
    highlight === 'construction'
      ? 'bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
      : highlight === 'other'
        ? 'bg-amber-100/80 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
        : highlight === 'info'
          ? 'bg-sky-100/80 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300'
          : highlight === 'neutral'
            ? 'bg-slate-100/80 text-slate-700 dark:bg-slate-800/50 dark:text-slate-300'
            : 'bg-primary/10 text-primary'

  const renderDropdownContent = () => {
    if (sections && sections.length > 0) {
      const hasAny = sections.some((s) => s.users.length > 0)
      if (!hasAny) {
        return <div className="px-2 py-3 text-sm text-muted-foreground">No volunteers</div>
      }
      return (
        <>
          {sections.map((section, idx) => (
            <React.Fragment key={section.label}>
              {idx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel className="text-muted-foreground font-bold">
                {section.label}
              </DropdownMenuLabel>
              {section.users.length === 0 ? (
                <div className="px-2 py-1 text-xs text-muted-foreground">None</div>
              ) : (
                section.users.map((user) => (
                  <DropdownMenuItem
                    key={user.id}
                    disabled={!dropdownClickable}
                    onClick={() => dropdownClickable && onSelectUser(user)}
                  >
                    {displayName(user)}
                  </DropdownMenuItem>
                ))
              )}
            </React.Fragment>
          ))}
        </>
      )
    }
    if (users.length === 0) {
      return <div className="px-2 py-3 text-sm text-muted-foreground">No volunteers</div>
    }
    return users.map((user) => (
      <DropdownMenuItem
        key={user.id}
        disabled={!dropdownClickable}
        onClick={() => dropdownClickable && onSelectUser(user)}
      >
        {displayName(user)}
      </DropdownMenuItem>
    ))
  }

  const cardContent = (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-muted-foreground text-sm">{title}</p>
        <p className="text-lg font-semibold">{countNode ?? count}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <div className={cn('rounded-full p-3 w-12 aspect-square flex items-center justify-center', iconHighlightClass)}>
          {icon}
        </div>
        {!hideDropdown && <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
      </div>
    </>
  )

  if (hideDropdown) {
    if (isMobile) {
      return (
        <div className={cn('rounded-lg p-4 w-full flex justify-between items-center gap-5 border min-w-0 shadow-sm', cardHighlightClass)}>
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">{title}</p>
            <p className="text-lg font-semibold">{countNode ?? count}</p>
          </div>
          <div className={cn('rounded-full p-3 w-12 aspect-square flex items-center justify-center shrink-0', iconHighlightClass)}>
            {icon}
          </div>
        </div>
      )
    }
    return (
      <div className={cn('rounded p-4 w-full flex justify-between items-center gap-5 border min-w-0', cardHighlightClass)}>
        {cardContent}
      </div>
    )
  }

  return (
    <DropdownMenu>
      {isMobile ? (
        <>
          <div
            className={cn('rounded-lg p-4 w-full flex justify-between items-center gap-5 border min-w-0 shadow-sm', cardHighlightClass)}
          >
          <div className="min-w-0 flex-1">
            <p className="text-muted-foreground text-sm">{title}</p>
            <p className="text-lg font-semibold">{countNode ?? count}</p>
          </div>
            <div className="flex items-center gap-1 shrink-0">
              <div className={cn('rounded-full p-3 w-12 aspect-square flex items-center justify-center', iconHighlightClass)}>
                {icon}
              </div>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`View ${title} volunteers`}
                  className="touch-manipulation p-2 -m-2 rounded-md cursor-pointer hover:bg-muted/50 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 flex items-center justify-center"
                >
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              </DropdownMenuTrigger>
            </div>
          </div>
          <DropdownMenuContent align="end" className="max-h-[min(16rem,50vh)] overflow-y-auto">
            {renderDropdownContent()}
          </DropdownMenuContent>
        </>
      ) : (
        <>
          <DropdownMenuTrigger asChild>
            <div
              role="button"
              tabIndex={0}
              aria-label={`View ${title} volunteers`}
              className={cn('rounded p-4 w-full flex justify-between items-center gap-5 border min-w-0 cursor-pointer hover:bg-muted/50 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', cardHighlightClass)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  ;(e.currentTarget as HTMLElement).click()
                }
              }}
            >
              {cardContent}
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[min(16rem,50vh)] overflow-y-auto">
            {renderDropdownContent()}
          </DropdownMenuContent>
        </>
      )}
    </DropdownMenu>
  )
}

function downloadParticipantsCsv(users: VolunteerTableRow[]) {
  const headers = ['Name', 'SR User ID', 'Organization', 'Added']
  const rows = users.map((u) => [
    u.fullname ?? '',
    u.volunteer?.srUserId != null ? String(u.volunteer.srUserId) : '',
    u.volunteer?.organizationName ?? '',
    u.joinedAt ? format(new Date(u.joinedAt), 'yyyy-MM-dd') : '',
  ])
  const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))].join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `participants-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

export function GroupParticipantsTab({ group, isVolunteerView = false }: GroupParticipantsTabProps) {
  const { session } = useSession()
  const currentUserId = session?.sub != null ? Number(session.sub) : null
  const isVolunteerRole = session?.role === UserRole.VOLUNTEER
  const isAdminOrBaseLeader =
    session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER
  const isGroupLeaderById =
    currentUserId != null && group.contactUserId != null && Number(group.contactUserId) === Number(currentUserId)
  const isGroupLeaderByEmail =
    !!session?.email?.trim() &&
    !!group.contact_email?.trim() &&
    session.email.trim().toLowerCase() === group.contact_email.trim().toLowerCase()
  const isGroupLeader = isGroupLeaderById || isGroupLeaderByEmail
  const { regionId } = useRegionId()
  const { regionSlug } = useRegion()
  const { data: tableRowsResponse, isLoading } = useVolunteerTableRowsForGroup(group.id)
  /** Group volunteers from API; backend includes group leader (contact) when set, even if not yet in group_volunteers. */
  const groupVolunteers: VolunteerTableRow[] = useMemo(
    () =>
      tableRowsResponse?.success && tableRowsResponse?.data?.users
        ? tableRowsResponse.data.users
        : [],
    [tableRowsResponse]
  )
  const isTeamLeaderGroupMember = useMemo(() => {
    if (currentUserId == null) return false
    return groupVolunteers.some((u) => {
      const roleName = ((u.role as { name?: string } | undefined)?.name ?? '').replace(/\s+/g, '').toLowerCase()
      return Number(u.id) === Number(currentUserId) && roleName === UserRole.TEAM_LEADER.toLowerCase()
    })
  }, [currentUserId, groupVolunteers])
  const isGroupScopedLeader = isGroupLeader || isTeamLeaderGroupMember
  /** Admin, Base Leader, group contact, or this group's TeamLeader member sees full participant details. */
  const viewerSeesFullParticipantNames = isAdminOrBaseLeader || isGroupScopedLeader
  const canManageParticipants =
    !isVolunteerView && (isAdminOrBaseLeader || isGroupScopedLeader)
  const canViewNotes = canManageParticipants
  const showFlightDetailsColumn = canViewNotes && regionSlug?.toLowerCase() === 'jamaica'
  const removeGroupVolunteer = useRemoveGroupVolunteer(group.id)
  const isMobile = useIsMobile()
  const [search, setSearch] = useState('')
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false)
  const [updateUserTargetId, setUpdateUserTargetId] = useState<number | null>(null)
  const [fetchedUser, setFetchedUser] = useState<NonHomeownerUser | null>(null)
  const [fetchUserLoading, setFetchUserLoading] = useState(false)
  const [fetchUserError, setFetchUserError] = useState<string | null>(null)

  const canUpdateTargetUser = useCallback((userId: number) => {
    if (isVolunteerRole && !isGroupScopedLeader) {
      return currentUserId != null && Number(userId) === Number(currentUserId)
    }
    return canManageParticipants
  }, [isVolunteerRole, isGroupScopedLeader, currentUserId, canManageParticipants])

  useEffect(() => {
    if (!updateUserTargetId) {
      setFetchedUser(null)
      setFetchUserError(null)
      return
    }
    if (!canUpdateTargetUser(updateUserTargetId)) {
      setUpdateUserTargetId(null)
      return
    }
    setFetchUserLoading(true)
    setFetchUserError(null)
    setFetchedUser(null)
    getNonHomeownerUserById(updateUserTargetId)
      .then((res) => {
        if (res.success && res.data) setFetchedUser(res.data)
        else setFetchUserError(!res.success ? res.message : 'Failed to load user')
      })
      .catch(() => setFetchUserError('Failed to load user'))
      .finally(() => setFetchUserLoading(false))
  }, [updateUserTargetId, canUpdateTargetUser])
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [expandedNotesIds, setExpandedNotesIds] = useState<Set<number>>(new Set())
  // Column filters (same pattern as Volunteers Filter/Search)
  const [volunteerNameFilter, setVolunteerNameFilter] = useState('')
  const [organizationFilter, setOrganizationFilter] = useState('')
  const [roleFilter, setRoleFilter] = useState<string[]>([])
  const [genderFilter, setGenderFilter] = useState<string[]>([])
  useEffect(() => {
    if (!isVolunteerRole) return
    if (genderFilter.length === 0) return
    setGenderFilter([])
  }, [isVolunteerRole, genderFilter.length])
  const [arrivalDateFilter, setArrivalDateFilter] = useState('')
  const [departureDateFilter, setDepartureDateFilter] = useState('')
  const [addedFromFilter, setAddedFromFilter] = useState('')
  const [addedToFilter, setAddedToFilter] = useState('')
  const [regionIdFilter, setRegionIdFilter] = useState('')
  const [lastActiveFromFilter, setLastActiveFromFilter] = useState('')
  const [lastActiveToFilter, setLastActiveToFilter] = useState('')
  const [ageFilter, setAgeFilter] = useState<string[]>([])
  const [regionOptions, setRegionOptions] = useState<Array<{ value: string; label: string }>>([])
  const [experienceSkillFilter, setExperienceSkillFilter] = useState<string[]>([])
  const [skillRatingFilters, setSkillRatingFilters] = useState<Record<string, string[]>>({})
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const filterSnapshotRef = useRef<{
    volunteerNameFilter: string
    roleFilter: string[]
    genderFilter: string[]
    ageFilter: string[]
    experienceSkillFilter: string[]
    skillRatingFilters: Record<string, string[]>
    lastActiveFromFilter: string
    lastActiveToFilter: string
    addedFromFilter: string
    addedToFilter: string
  } | null>(null)
  const isApplyingFiltersRef = useRef(false)
  const groupVolunteerIds = useMemo(
    () => [...new Set(groupVolunteers.map((u) => u.id))].sort((a, b) => a - b),
    [groupVolunteers]
  )
  const { data: hydratedVolunteerMetaByUserId = {} } = useQuery({
    queryKey: ['stm', 'group', group.id, 'skills-notes', groupVolunteerIds.join(',')],
    queryFn: async () => {
      const entries = await Promise.all(
        groupVolunteerIds.map(async (userId) => {
          const res = await getNonHomeownerUserById(userId)
          const volunteer = res.success
            ? (res.data?.volunteer as unknown as { skillsAndQualities?: string; jamaicaTripFlightDetails?: string } | undefined)
            : undefined
          const note = volunteer?.skillsAndQualities ?? ''
          const flightDetails = volunteer?.jamaicaTripFlightDetails ?? ''
          return [
            userId,
            {
              note: typeof note === 'string' ? note.trim() : '',
              flightDetails: typeof flightDetails === 'string' ? flightDetails.trim() : '',
              dateOfBirth: res.success ? (res.data?.dateOfBirth ?? null) : null,
            },
          ] as const
        })
      )
      return Object.fromEntries(entries) as Record<number, HydratedVolunteerMeta>
    },
    enabled: groupVolunteerIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  const getVolunteerAge = useCallback((user: VolunteerTableRow): number | null => {
    const hydratedDob = hydratedVolunteerMetaByUserId[user.id]?.dateOfBirth ?? null
    return getAgeFromDateOfBirth(user.dateOfBirth ?? hydratedDob)
  }, [hydratedVolunteerMetaByUserId])

  useEffect(() => {
    authFetchJson<any[]>('/regions/options')
      .then((res) => {
        if (res.success && Array.isArray(res.data)) {
          setRegionOptions(
            res.data.map((r: { id: string | number; label: string }) => ({
              value: String(r.id),
              label: r.label ?? String(r.id),
            }))
          )
        }
      })
      .catch(() => setRegionOptions([]))
  }, [])

  const rows = useMemo(() => {
    return groupVolunteers.map((u) => buildUserRow(u))
  }, [groupVolunteers])

  const volunteerNameSuggestions = useMemo((): string[] => {
    const names = rows
      .map((r: UserRow) => r.volunteerName)
      .filter((x: string): x is string => Boolean(x))
    return [...new Set(names)].sort() as string[]
  }, [rows])

  const { data: orgNamesData } = useVolunteerOrganizationNames()
  const organizationSuggestions = useMemo(
    () => (orgNamesData?.success ? (orgNamesData.data ?? []) : []),
    [orgNamesData]
  )

  const handleNameClick = useCallback((user: VolunteerTableRow) => {
    if (!canUpdateTargetUser(user.id)) return
    setUpdateUserTargetId(user.id)
  }, [canUpdateTargetUser])

  /** All group volunteers are ReliefHub users (for skills banner). */
  const linkedUsersInGroup = useMemo(() => groupVolunteers, [groupVolunteers])

  /** True if user has any construction-related skill rated 3, 4, or 5 (user or volunteer fields). */
  const hasConstructionSkill3Plus = useCallback((u: VolunteerTableRow) => {
    const v = u.volunteer
    const rating = (n: number | undefined | null) => typeof n === 'number' && n >= 3
    return (
      rating(u.experienceConstruction) ||
      rating(v?.experienceDrywallFinishing) ||
      rating(v?.experienceRoughCarpentry) ||
      rating(v?.experienceFinishCarpentry) ||
      rating(v?.experienceFlooring) ||
      rating(v?.experienceWoodWorking) ||
      rating(v?.experienceElectrical) ||
      rating(v?.experiencePlumbing) ||
      rating(v?.experienceHvac) ||
      rating(v?.experienceArborist) ||
      rating(v?.experienceRoofing) ||
      rating(v?.experienceHeavyEquipment)
    )
  }, [])

  /** True if user has any construction-related volunteer field rated 1 or 2 (general interest, not skilled). */
  const hasConstructionRating1or2 = useCallback((u: VolunteerTableRow) => {
    const v = u.volunteer
    const oneOrTwo = (n: number | undefined | null) => n === 1 || n === 2
    return (
      oneOrTwo(v?.experienceDrywallFinishing) ||
      oneOrTwo(v?.experienceRoughCarpentry) ||
      oneOrTwo(v?.experienceFinishCarpentry) ||
      oneOrTwo(v?.experienceFlooring) ||
      oneOrTwo(v?.experienceWoodWorking) ||
      oneOrTwo(v?.experienceElectrical) ||
      oneOrTwo(v?.experiencePlumbing) ||
      oneOrTwo(v?.experienceHvac) ||
      oneOrTwo(v?.experienceArborist) ||
      oneOrTwo(v?.experienceRoofing) ||
      oneOrTwo(v?.experienceHeavyEquipment)
    )
  }, [])

  /** Construction: rated 3, 4, or 5 on any construction experience field. */
  const constructionSkillsUsers = useMemo(() => {
    return linkedUsersInGroup.filter(hasConstructionSkill3Plus)
  }, [linkedUsersInGroup, hasConstructionSkill3Plus])

  /** Other skills: manual labor interest ≥1, any Alt Ministries ≥1, or (if no construction 3+) data/storytelling ≥1 or construction 1/2. */
  const otherSkillsUsers = useMemo(() => {
    return linkedUsersInGroup.filter((u: VolunteerTableRow) => {
      const manualLabor = getExperienceRating(u, 'experienceManualLabor') ?? 0
      if (manualLabor >= 1) return true
      const altMin = getExperienceRating(u, 'experienceAltMinistries') ?? 0
      if (altMin >= 1) return true
      if (hasConstructionSkill3Plus(u)) return false
      const dm = getExperienceRating(u, 'experienceDataManagement') ?? 0
      const st = getExperienceRating(u, 'experienceStorytelling') ?? 0
      return dm >= 1 || st >= 1 || hasConstructionRating1or2(u)
    })
  }, [linkedUsersInGroup, hasConstructionSkill3Plus, hasConstructionRating1or2])
  const constructionSkillUserIds = useMemo(
    () => new Set(constructionSkillsUsers.map((u) => u.id)),
    [constructionSkillsUsers]
  )
  const otherSkillUserIds = useMemo(
    () => new Set(otherSkillsUsers.map((u) => u.id)),
    [otherSkillsUsers]
  )

  /** Team leads: rated 3, 4, or 5 on leading at a work site. */
  const teamLeadsUsers = useMemo(() => {
    return linkedUsersInGroup.filter((u: VolunteerTableRow) => {
      const rating = getExperienceRating(u, 'experienceLeadingTeams')
      return typeof rating === 'number' && rating >= 3
    })
  }, [linkedUsersInGroup])

  /** Interested in cooking: mealPreparationAbility indicates interest (yes, willing, experienced, or 3–5). */
  const cookingInterestedUsers = useMemo(() => {
    return linkedUsersInGroup.filter((u: VolunteerTableRow) => {
      const mp = (u.mealPreparationAbility ?? u.volunteer?.mealPreparationAbility ?? '').toString().trim().toLowerCase()
      if (!mp) return false
      if (mp === 'no' || mp === 'prefer_not') return false
      if (mp === 'yes' || mp === 'willing' || mp === 'experienced') return true
      const n = parseInt(mp, 10)
      return !Number.isNaN(n) && n >= 3
    })
  }, [linkedUsersInGroup])

  /** Dietary restrictions: user or volunteer has non-empty dietaryRestrictions string (exclude NA, N/A, None). */
  const dietaryRestrictionsUsers = useMemo(() => {
    const excludeValues = ['na', 'n/a', 'none']
    return linkedUsersInGroup.filter((u: VolunteerTableRow) => {
      const dr = u.dietaryRestrictions ?? u.volunteer?.dietaryRestrictions ?? ''
      if (typeof dr !== 'string') return false
      const trimmed = dr.trim()
      if (trimmed.length === 0) return false
      if (excludeValues.includes(trimmed.toLowerCase())) return false
      return true
    })
  }, [linkedUsersInGroup])

  /** Males: gender is "male" (user or volunteer). */
  const malesUsers = useMemo(() => {
    return linkedUsersInGroup.filter(
      (u: VolunteerTableRow) => (u.gender ?? u.volunteer?.gender ?? '').toString().toLowerCase() === 'male'
    )
  }, [linkedUsersInGroup])

  /** Females: gender is "female" (user or volunteer). */
  const femalesUsers = useMemo(() => {
    return linkedUsersInGroup.filter(
      (u: VolunteerTableRow) => (u.gender ?? u.volunteer?.gender ?? '').toString().toLowerCase() === 'female'
    )
  }, [linkedUsersInGroup])

  const filtered = useMemo(() => {
    let list: UserRow[] = rows
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      list = list.filter(
        (r) =>
          r.volunteerName.toLowerCase().includes(q) ||
          (r.user.email ?? '').toLowerCase().includes(q) ||
          (r.user.phone ?? '').toLowerCase().includes(q) ||
          (r.user.volunteer?.organizationName ?? '').toLowerCase().includes(q)
      )
    }
    if (volunteerNameFilter.trim()) {
      const q = volunteerNameFilter.trim().toLowerCase()
      list = list.filter((r) => r.volunteerName.toLowerCase().includes(q))
    }
    if (organizationFilter.trim()) {
      const q = organizationFilter.trim().toLowerCase()
      list = list.filter((r) => r.org.toLowerCase().includes(q))
    }
    if (roleFilter.length) {
      list = list.filter((r) => roleFilter.includes(r.role))
    }
    if (!isVolunteerRole && genderFilter.length > 0) {
      list = list.filter((r) => {
        const g = (r.user.gender ?? r.user.volunteer?.gender ?? '').toString().trim().toLowerCase()
        return g ? genderFilter.includes(g) : false
      })
    }
    if (arrivalDateFilter) {
      list = list.filter((r) => r.arrivalStr === arrivalDateFilter)
    }
    if (departureDateFilter) {
      list = list.filter((r) => r.departureStr === departureDateFilter)
    }
    if (addedFromFilter) {
      list = list.filter((r) => r.addedStr >= addedFromFilter)
    }
    if (addedToFilter) {
      list = list.filter((r) => r.addedStr <= addedToFilter)
    }
    if (regionIdFilter) {
      list = list.filter((r) => r.regionId === regionIdFilter)
    }
    if (lastActiveFromFilter) {
      list = list.filter((r) => r.lastActiveStr >= lastActiveFromFilter)
    }
    if (lastActiveToFilter) {
      list = list.filter((r) => r.lastActiveStr <= lastActiveToFilter)
    }
    if (canManageParticipants && ageFilter.length > 0) {
      list = list.filter((r) => {
        const age = getVolunteerAge(r.user)
        if (age == null) return false
        return ageFilter.some((bucket) => matchesAgeBucket(age, bucket))
      })
    }
    if (experienceSkillFilter.length > 0) {
      list = list.filter((r) => {
        const u = r.user as Record<string, unknown>
        return experienceSkillFilter.some((skillKey) => {
          const raw = u[skillKey] ?? (u.volunteer as Record<string, unknown> | undefined)?.[skillKey]
          if (typeof raw === 'number') return raw >= 1
          if (skillKey === 'mealPreparationAbility' && typeof raw === 'string') {
            const s = raw.trim().toLowerCase()
            if (s === 'no' || s === 'prefer_not') return false
            if (s === 'yes' || s === 'willing' || s === 'experienced') return true
            const n = parseInt(raw, 10)
            return !Number.isNaN(n) && n >= 1
          }
          return false
        })
      })
    }
    const activeSkillFilters = Object.entries(skillRatingFilters).filter(([, v]) => Array.isArray(v) && v.length > 0)
    if (activeSkillFilters.length > 0) {
      list = list.filter((r) =>
        activeSkillFilters.every(([columnKey, ratingValues]) => {
          const col = SKILL_COLUMNS.find((c) => c.key === columnKey)
          if (!col) return true
          const rating = getSkillColumnRating(r.user, col)
          return rating != null && ratingValues.includes(String(rating))
        })
      )
    }
    return list
  }, [
    rows,
    search,
    group,
    volunteerNameFilter,
    organizationFilter,
    roleFilter,
    isVolunteerRole,
    genderFilter,
    arrivalDateFilter,
    departureDateFilter,
    addedFromFilter,
    addedToFilter,
    regionIdFilter,
    lastActiveFromFilter,
    lastActiveToFilter,
    canManageParticipants,
    ageFilter,
    getVolunteerAge,
    experienceSkillFilter,
    skillRatingFilters,
  ])

  const handleDelete = useCallback(
    (user: VolunteerTableRow) => {
      if (confirm(`Remove ${user.fullname ?? user.email} from this trip?`)) {
        removeGroupVolunteer.mutate(user.id)
      }
    },
    [removeGroupVolunteer]
  )

  const toggleSelected = useCallback((userId: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filtered.map((r) => r.user.id)))
    }
  }, [filtered, selectedIds.size])

  /** Selected volunteers (for group email). */
  const selectedParticipants = useMemo(
    () => filtered.filter((r) => selectedIds.has(r.user.id)),
    [filtered, selectedIds]
  )

  const skillFieldToColumnKey = useMemo(
    () => Object.fromEntries(SKILL_COLUMNS.map((c) => [c.field, c.key])) as Record<string, string>,
    []
  )

  const activePrintColumns = useMemo(() => {
    const active = new Set<string>()
    if (volunteerNameFilter.trim()) active.add('volunteer')
    if (roleFilter.length > 0) active.add('role')
    if (!isVolunteerRole && genderFilter.length > 0) active.add('gender')
    if (canManageParticipants && ageFilter.length > 0) active.add('age')
    if (experienceSkillFilter.length > 0) {
      active.add('experience-skill')
      experienceSkillFilter.forEach((skillField) => {
        const mappedColumnKey = skillFieldToColumnKey[skillField]
        if (mappedColumnKey) active.add(mappedColumnKey)
      })
    }
    if (lastActiveFromFilter || lastActiveToFilter) active.add('lastActive')
    if (addedFromFilter || addedToFilter) active.add('added')
    Object.entries(skillRatingFilters).forEach(([key, values]) => {
      if (Array.isArray(values) && values.length > 0) active.add(key)
    })
    return active
  }, [
    volunteerNameFilter,
    roleFilter,
    isVolunteerRole,
    genderFilter,
    canManageParticipants,
    ageFilter,
    experienceSkillFilter,
    lastActiveFromFilter,
    lastActiveToFilter,
    addedFromFilter,
    addedToFilter,
    skillRatingFilters,
    skillFieldToColumnKey,
  ])

  const shouldPrintColumn = useCallback((key: string) => {
    if (activePrintColumns.size === 0) return DEFAULT_PRINT_COLUMNS.has(key)
    if (key === 'volunteer' || key === 'srUserId') return true
    return activePrintColumns.has(key)
  }, [activePrintColumns])

  const isColumnFiltered = useCallback((key: string) => activePrintColumns.has(key), [activePrintColumns])
  const printFilterSummary = useMemo(() => {
    const lines: string[] = []
    if (volunteerNameFilter.trim()) lines.push(`Volunteer: ${volunteerNameFilter.trim()}`)
    if (roleFilter.length > 0) lines.push(`Role: ${roleFilter.join(', ')}`)
    if (!isVolunteerRole && genderFilter.length > 0) lines.push(`Gender: ${genderFilter.join(', ')}`)
    if (canManageParticipants && ageFilter.length > 0) {
      const labels = ageFilter.map((value) => AGE_FILTER_OPTIONS.find((option) => option.value === value)?.label ?? value)
      lines.push(`Age: ${labels.join(', ')}`)
    }
    if (experienceSkillFilter.length > 0) {
      const labels = experienceSkillFilter.map((skillKey) =>
        ALL_SKILLS_FILTER_OPTIONS.find((o) => o.value === skillKey)?.label ?? skillKey
      )
      lines.push(`All skills filter: ${labels.join(', ')}`)
    }
    Object.entries(skillRatingFilters).forEach(([key, values]) => {
      if (!Array.isArray(values) || values.length === 0) return
      const label = SKILL_COLUMNS.find((c) => c.key === key)?.label ?? key
      lines.push(`${label}: ${values.join(', ')}`)
    })
    if (lastActiveFromFilter || lastActiveToFilter) {
      lines.push(`Last Active: ${lastActiveFromFilter || '...'} to ${lastActiveToFilter || '...'}`)
    }
    if (addedFromFilter || addedToFilter) {
      lines.push(`Added: ${addedFromFilter || '...'} to ${addedToFilter || '...'}`)
    }
    return lines
  }, [
    volunteerNameFilter,
    roleFilter,
    isVolunteerRole,
    genderFilter,
    canManageParticipants,
    ageFilter,
    experienceSkillFilter,
    skillRatingFilters,
    lastActiveFromFilter,
    lastActiveToFilter,
    addedFromFilter,
    addedToFilter,
  ])

  const getEmailForUser = useCallback((u: VolunteerTableRow): string => {
    return (u.email ?? '').trim() || ''
  }, [])

  const handleGroupEmail = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const emails = selectedParticipants
        .map((r) => getEmailForUser(r.user))
        .filter((addr): addr is string => !!addr?.trim())
      const deduped = [...new Set(emails)]
      if (deduped.length === 0) {
        toast.error('No email addresses for selected participants. Add emails in Update Volunteer.')
        return
      }
      const bcc = deduped.map((addr) => encodeURIComponent(addr)).join(',')
      window.location.href = `mailto:?bcc=${bcc}`
    },
    [selectedParticipants, getEmailForUser]
  )

  const canSendGroupEmail =
    selectedParticipants.length > 0 &&
    selectedParticipants.some((r) => !!getEmailForUser(r.user)?.trim())

  const openUpdateUser = useCallback(
    (user: VolunteerTableRow) => {
      if (!canUpdateTargetUser(user.id)) return
      setUpdateUserTargetId(user.id)
    },
    [canUpdateTargetUser]
  )

  const clearAllColumnFilters = useCallback(() => {
    setVolunteerNameFilter('')
    setOrganizationFilter('')
    setRoleFilter([])
    setGenderFilter([])
    setArrivalDateFilter('')
    setDepartureDateFilter('')
    setAddedFromFilter('')
    setAddedToFilter('')
    setRegionIdFilter('')
    setLastActiveFromFilter('')
    setLastActiveToFilter('')
    setAgeFilter([])
    setExperienceSkillFilter([])
    setSkillRatingFilters({})
    setSortColumn(null)
    setSortDirection('asc')
  }, [])

  const openFilterDialog = useCallback(() => {
    filterSnapshotRef.current = {
      volunteerNameFilter,
      roleFilter: [...roleFilter],
      genderFilter: [...genderFilter],
      ageFilter: [...ageFilter],
      experienceSkillFilter: [...experienceSkillFilter],
      skillRatingFilters: Object.fromEntries(
        Object.entries(skillRatingFilters).map(([k, v]) => [k, Array.isArray(v) ? [...v] : []])
      ),
      lastActiveFromFilter,
      lastActiveToFilter,
      addedFromFilter,
      addedToFilter,
    }
    setIsFilterDialogOpen(true)
  }, [
    volunteerNameFilter,
    roleFilter,
    genderFilter,
    ageFilter,
    experienceSkillFilter,
    skillRatingFilters,
    lastActiveFromFilter,
    lastActiveToFilter,
    addedFromFilter,
    addedToFilter,
  ])

  const cancelFilterDialog = useCallback(() => {
    const snap = filterSnapshotRef.current
    if (snap) {
      setVolunteerNameFilter(snap.volunteerNameFilter)
      setRoleFilter(snap.roleFilter)
      setGenderFilter(snap.genderFilter)
      setAgeFilter(snap.ageFilter)
      setExperienceSkillFilter(snap.experienceSkillFilter)
      setSkillRatingFilters(snap.skillRatingFilters)
      setLastActiveFromFilter(snap.lastActiveFromFilter)
      setLastActiveToFilter(snap.lastActiveToFilter)
      setAddedFromFilter(snap.addedFromFilter)
      setAddedToFilter(snap.addedToFilter)
    }
    setIsFilterDialogOpen(false)
  }, [])

  const hasAnyColumnFilterActive =
    !!volunteerNameFilter ||
    !!organizationFilter ||
    roleFilter.length > 0 ||
    (!isVolunteerRole && genderFilter.length > 0) ||
    (canManageParticipants && ageFilter.length > 0) ||
    !!arrivalDateFilter ||
    !!departureDateFilter ||
    !!addedFromFilter ||
    !!addedToFilter ||
    !!regionIdFilter ||
    !!lastActiveFromFilter ||
    !!lastActiveToFilter ||
    experienceSkillFilter.length > 0 ||
    Object.values(skillRatingFilters).some((v) => Array.isArray(v) && v.length > 0)

  const normalizeOptionalNoteText = useCallback((value: string | null | undefined): string => {
    const text = (value ?? '').trim()
    if (!text) return ''
    const normalized = text.toLowerCase()
    const excluded = new Set([
      'none',
      'no',
      'n/a',
      'na',
      'n.a.',
      'not applicable',
      'nil',
      'nope',
      'none.',
      'no.',
    ])
    return excluded.has(normalized) ? '' : text
  }, [])

  const getVolunteerNotes = useCallback((user: VolunteerTableRow): string => {
    const hydratedNote = hydratedVolunteerMetaByUserId[user.id]?.note?.trim() || ''
    const rowSkillsNote = (user as unknown as { skillsAndQualities?: string }).skillsAndQualities ?? ''
    const volunteerSkillsNote =
      (user.volunteer as unknown as { skillsAndQualities?: string } | undefined)?.skillsAndQualities ?? ''
    const whatElseRaw = hydratedNote || rowSkillsNote || volunteerSkillsNote || ''
    const whatElse = normalizeOptionalNoteText(typeof whatElseRaw === 'string' ? whatElseRaw : '')
    const dietaryRaw =
      user.dietaryRestrictions ??
      user.volunteer?.dietaryRestrictions ??
      ''
    const dietary = normalizeOptionalNoteText(typeof dietaryRaw === 'string' ? dietaryRaw : '')
    const parts: string[] = []
    if (dietary) parts.push(`Dietary: ${dietary}`)
    if (whatElse) parts.push(`Other: ${whatElse}`)
    return parts.join(' | ')
  }, [hydratedVolunteerMetaByUserId, normalizeOptionalNoteText])

  const getVolunteerFlightDetails = useCallback((user: VolunteerTableRow): string => {
    const hydratedFlightDetails = hydratedVolunteerMetaByUserId[user.id]?.flightDetails?.trim() || ''
    const rowFlightDetails = (user as unknown as { jamaicaTripFlightDetails?: string }).jamaicaTripFlightDetails ?? ''
    const volunteerFlightDetails =
      (user.volunteer as unknown as { jamaicaTripFlightDetails?: string } | undefined)?.jamaicaTripFlightDetails ?? ''
    const raw = hydratedFlightDetails || rowFlightDetails || volunteerFlightDetails || ''
    return normalizeOptionalNoteText(typeof raw === 'string' ? raw : '')
  }, [hydratedVolunteerMetaByUserId, normalizeOptionalNoteText])

  const handleSortColumn = useCallback((columnKey: string) => {
    if (sortColumn !== columnKey) {
      setSortColumn(columnKey)
      setSortDirection('desc')
      return
    }
    if (sortDirection === 'desc') {
      setSortDirection('asc')
      return
    }
    setSortColumn(null)
  }, [sortColumn, sortDirection])

  const sortedFiltered = useMemo(() => {
    const list = [...filtered]
    const singleSkill = experienceSkillFilter.length === 1 ? experienceSkillFilter[0] : null
    list.sort((a, b) => {
      const aLeader = isGroupLeaderUser(a.user, group)
      const bLeader = isGroupLeaderUser(b.user, group)
      // Pin group contact to the top only in the default (unsorted) view so explicit column sort is purely numeric/textual.
      if (!sortColumn) {
        if (aLeader && !bLeader) return -1
        if (!aLeader && bLeader) return 1
        if (singleSkill) {
          const ra = getExperienceRating(a.user, singleSkill) ?? 0
          const rb = getExperienceRating(b.user, singleSkill) ?? 0
          return rb - ra
        }
        return 0
      }
      const c = compareUserRowsForSort(
        a,
        b,
        sortColumn,
        getVolunteerAge,
        getVolunteerNotes,
        getVolunteerFlightDetails,
        singleSkill
      )
      return sortDirection === 'asc' ? c : -c
    })
    return list
  }, [
    filtered,
    sortColumn,
    sortDirection,
    experienceSkillFilter,
    group,
    getVolunteerAge,
    getVolunteerNotes,
    getVolunteerFlightDetails,
  ])

  const toggleNotesExpanded = useCallback((userId: number) => {
    setExpandedNotesIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }, [])

  const displayNameForDropdown = useMemo(() => {
    if (!isVolunteerRole || currentUserId == null) return undefined
    if (viewerSeesFullParticipantNames) return undefined
    return (u: VolunteerTableRow) => {
      if (u.id === currentUserId) return (u.fullname ?? 'Unknown')
      if (isGroupLeaderUser(u, group)) return (u.fullname ?? 'Unknown')
      return maskHomeownerLastName(u.fullname ?? '') || 'Unknown'
    }
  }, [isVolunteerRole, currentUserId, group, viewerSeesFullParticipantNames])

  const groupDateRangeLabel = useMemo(() => {
    const start = parseDateValue(group.start_date)
    const end = parseDateValue(group.end_date)
    if (start && end) {
      return `${format(start, 'MM/dd/yyyy')}-${format(end, 'MM/dd/yyyy')}`
    }
    return `${group.start_date ?? '—'}-${group.end_date ?? '—'}`
  }, [group.start_date, group.end_date])

  const singleExperienceSkillFilter = experienceSkillFilter.length === 1 ? experienceSkillFilter[0] : ''

  return (
    <div className="border rounded px-3 py-5 space-y-4">
      <div className="flex flex-col gap-4">
        {/* Skills breakdown banner (Reports-style cards) */}
        <div className="flex flex-col gap-4 md:flex-row md:gap-3 print:hidden">
          <SkillsBannerCard
            title="Construction Skills"
            count={constructionSkillsUsers.length}
            icon={<HardHat className="h-5 w-5" />}
            highlight={isAdminOrBaseLeader ? 'construction' : undefined}
            users={constructionSkillsUsers}
            onSelectUser={openUpdateUser}
            displayNameForDropdown={displayNameForDropdown}
            dropdownClickable={canManageParticipants}
          />
          <SkillsBannerCard
            title="Other Skills"
            count={otherSkillsUsers.length}
            icon={<Sparkles className="h-5 w-5" />}
            highlight={isAdminOrBaseLeader ? 'other' : undefined}
            users={otherSkillsUsers}
            onSelectUser={openUpdateUser}
            displayNameForDropdown={displayNameForDropdown}
            dropdownClickable={canManageParticipants}
          />
          <SkillsBannerCard
            title="Potential Team Leads"
            count={teamLeadsUsers.length}
            icon={<UserCheck className="h-5 w-5" />}
            highlight={isAdminOrBaseLeader ? 'construction' : undefined}
            users={teamLeadsUsers}
            onSelectUser={openUpdateUser}
            displayNameForDropdown={displayNameForDropdown}
            dropdownClickable={canManageParticipants}
          />
          <SkillsBannerCard
            title="Interested in cooking"
            count={cookingInterestedUsers.length}
            icon={<ChefHat className="h-5 w-5" />}
            highlight={isAdminOrBaseLeader ? 'info' : undefined}
            users={cookingInterestedUsers}
            onSelectUser={openUpdateUser}
            displayNameForDropdown={displayNameForDropdown}
            dropdownClickable={canManageParticipants}
          />
          <SkillsBannerCard
            title="Dietary restrictions"
            count={dietaryRestrictionsUsers.length}
            icon={<UtensilsCrossed className="h-5 w-5" />}
            highlight={isAdminOrBaseLeader ? 'info' : undefined}
            users={dietaryRestrictionsUsers}
            onSelectUser={openUpdateUser}
            displayNameForDropdown={displayNameForDropdown}
            dropdownClickable={canManageParticipants}
            hideDropdown={isVolunteerRole}
          />
          <SkillsBannerCard
            title="Males / Females"
            count={malesUsers.length + femalesUsers.length}
            countNode={<>{malesUsers.length} / {femalesUsers.length}</>}
            icon={<Users className="h-5 w-5" />}
            highlight={isAdminOrBaseLeader ? 'neutral' : undefined}
            users={[...malesUsers, ...femalesUsers]}
            sections={[{ label: 'Male', users: malesUsers }, { label: 'Female', users: femalesUsers }]}
            onSelectUser={openUpdateUser}
            displayNameForDropdown={displayNameForDropdown}
            dropdownClickable={canManageParticipants}
            hideDropdown={isVolunteerRole}
          />
        </div>
        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_auto] gap-3 items-center print-hidden">
          <div className="flex flex-wrap gap-2 sm:gap-3 items-center justify-start min-w-0 w-full">
            {canManageParticipants && (
              <div className="flex items-center gap-2 shrink-0">
                <Checkbox
                  checked={filtered.length > 0 && selectedIds.size === filtered.length}
                  onCheckedChange={toggleSelectAll}
                  aria-label="Select all participants"
                  className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
                />
                <p className="text-xs text-muted-foreground whitespace-nowrap">
                  Showing {filtered.length} volunteer{filtered.length !== 1 ? 's' : ''}
                </p>
              </div>
            )}
            {!isMobile && (
              <div className="h-10 rounded-md border bg-background px-3 inline-flex items-center text-sm font-medium text-muted-foreground whitespace-nowrap shrink-0">
                {groupDateRangeLabel}
              </div>
            )}
            <Dialog
              open={isFilterDialogOpen}
              onOpenChange={(open) => {
                if (!open) {
                  if (isApplyingFiltersRef.current) {
                    isApplyingFiltersRef.current = false
                    setIsFilterDialogOpen(false)
                    return
                  }
                  cancelFilterDialog()
                  return
                }
                setIsFilterDialogOpen(true)
              }}
            >
              <DialogTrigger asChild>
                <Button
                  variant="default"
                  size="sm"
                  aria-label="Filter volunteers"
                  className={cn(
                    'bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10',
                    hasAnyColumnFilterActive && 'ring-2 ring-primary/50'
                  )}
                  onClick={openFilterDialog}
                >
                  <Filter className="h-3.5 w-3.5" />
                  <span className="hidden xl:inline">Filter</span>
                </Button>
              </DialogTrigger>
              <DialogContent
                className="max-h-[90vh] max-w-6xl"
                onInteractOutside={(e) => {
                  if ((e.target as HTMLElement)?.closest?.('[data-avoid-dialog-close]')) {
                    e.preventDefault()
                  }
                }}
              >
                <DialogTitle>Filter Group Volunteers</DialogTitle>
                <div className="overflow-y-auto max-h-[calc(90vh-180px)] pr-1 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    <div className="space-y-1.5">
                      <p className="text-xs font-medium text-muted-foreground">Volunteer</p>
                      <Input
                        value={volunteerNameFilter}
                        onChange={(e) => setVolunteerNameFilter((e.target as HTMLInputElement).value)}
                        placeholder="Search Group Volunteers"
                        className="h-9 border-input"
                        data-avoid-dialog-close
                      />
                    </div>
                    <FilterDropdownField
                      title="Role"
                      value={roleFilter}
                      onApply={(v) => setRoleFilter(v ?? [])}
                      options={roleFilterOptions}
                    />
                    {!isVolunteerRole && (
                      <FilterDropdownField
                        title="Gender"
                        value={genderFilter}
                        onApply={(v) => setGenderFilter(v ?? [])}
                        options={GENDER_FILTER_OPTIONS}
                      />
                    )}
                    {canManageParticipants && (
                      <FilterDropdownField
                        title="Age"
                        value={ageFilter}
                        onApply={(v) => setAgeFilter(v ?? [])}
                        options={AGE_FILTER_OPTIONS}
                      />
                    )}
                    <FilterDropdownField
                      title="All Skills"
                      value={experienceSkillFilter}
                      onApply={(v) => setExperienceSkillFilter(v ?? [])}
                      options={ALL_SKILLS_FILTER_OPTIONS}
                    />
                    {SKILL_COLUMNS.map((col) => (
                      <FilterDropdownField
                        key={`dialog-${col.key}`}
                        title={col.label}
                        value={skillRatingFilters[col.key] ?? []}
                        onApply={(v) => setSkillRatingFilters((prev) => ({ ...prev, [col.key]: v ?? [] }))}
                        options={RATING_FILTER_OPTIONS}
                      />
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={cancelFilterDialog}
                    showLoader={false}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    className="flex-1 bg-primary hover:bg-primary/90 text-white"
                    onClick={() => {
                      isApplyingFiltersRef.current = true
                      setIsFilterDialogOpen(false)
                    }}
                    showLoader={false}
                  >
                    Apply
                  </Button>
                </div>
                <div className="pt-1">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full"
                    onClick={clearAllColumnFilters}
                    showLoader={false}
                  >
                    Clear all filters
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-[120px] sm:w-[150px] h-10 font-medium rounded-md border bg-background justify-between"
                >
                  <span>All skills</span>
                  <ChevronDown className={cn('h-4 w-4 text-muted-foreground', experienceSkillFilter.length > 0 && 'text-primary')} />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-auto p-0">
                <RolesFilterContent
                  value={experienceSkillFilter}
                  onApply={(v) => setExperienceSkillFilter(v ?? [])}
                  options={ALL_SKILLS_FILTER_OPTIONS}
                />
              </PopoverContent>
            </Popover>
            <Button
              variant="default"
              size="sm"
              className="bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10"
              onClick={clearAllColumnFilters}
              disabled={!hasAnyColumnFilterActive && sortColumn === null}
              showLoader={false}
            >
              Clear All
            </Button>
            <div className="flex-1 min-w-[220px]">
              <Input
                placeholder="Search Group Volunteers"
                leftIcon={<Search className="text-muted-foreground text-sm w-4" />}
                className="h-10 rounded-md pl-9"
                value={search}
                onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              />
            </div>
          </div>
          {canManageParticipants && (
          <div className="flex flex-wrap xl:flex-nowrap items-center justify-start xl:justify-end gap-2 sm:gap-3 print-hidden">
            <AddVolunteerButton
              className="flex-shrink-0 [&>span]:hidden xl:[&>span]:inline"
              submitButtonLabel="Add to Group"
              groupId={group.id}
              allowForGroupLeader={isGroupScopedLeader}
              defaultServePeriod={
                group.start_date && group.end_date
                  ? {
                      from: group.start_date,
                      to: group.end_date,
                      regionId: group.regionId ?? group.region,
                    }
                  : undefined
              }
              defaultGroupOrganization={group.church_org?.trim() || undefined}
            />
            <Button
              variant="default"
              size="sm"
              className="bg-muted text-muted-foreground hover:bg-muted/80 font-bold rounded-md gap-2 py-1 h-10 flex-shrink-0"
              onClick={handleGroupEmail}
              disabled={selectedParticipants.length === 0}
              title={selectedParticipants.length > 0 ? (canSendGroupEmail ? `Email ${selectedParticipants.length} selected` : 'Selected participants have no email addresses') : 'Select participants to email'}
              showLoader={false}
            >
              <Mail className="w-5 h-5" />
              <span className="hidden sm:inline">Email</span>
            </Button>
            <Button
              variant="default"
              size="sm"
              className="bg-muted text-muted-foreground hover:bg-muted/80 font-bold rounded-md gap-2 py-1 h-10 flex-shrink-0"
              onClick={() => downloadParticipantsCsv(sortedFiltered.map((r) => r.user))}
              disabled={filtered.length === 0}
              showLoader={false}
            >
              <DownloadCloud className="w-5 h-5" />
              <span className="hidden sm:inline">Download</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-2 py-1 h-10"
              onClick={() => window.print()}
              type="button"
            >
              <Printer className="w-4 h-4" />
              <span className="hidden sm:inline">Print</span>
            </Button>
          </div>
          )}
          {!canManageParticipants && (
          <div className="flex items-center justify-end gap-3 print-hidden">
            <p className="text-xs text-muted-foreground whitespace-nowrap mr-auto">
              Showing {filtered.length} volunteer{filtered.length !== 1 ? 's' : ''}
            </p>
          </div>
          )}
        </div>
      </div>

      <div className="rounded-md border print:border-0">
        <div className="hidden print:block px-3 py-2 border-b print:px-0 print:py-1.5 print:mb-1">
          <p className="font-semibold print:text-sm">Filtered Volunteers</p>
          {printFilterSummary.length > 0 ? (
            <p className="text-sm mt-1 print:text-xs print:leading-snug">Filters: {printFilterSummary.join(' | ')}</p>
          ) : (
            <p className="text-sm mt-1 print:text-xs">Filters: none</p>
          )}
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-muted-foreground">
            {groupVolunteers.length === 0
              ? 'No volunteers added yet. Use "+ Add Volunteers" to add.'
              : 'No volunteers match your search.'}
          </div>
        ) : isMobile ? (
          <div className="p-4 space-y-4">
            {sortedFiltered.map((row) => {
              const user = row.user
              const volunteerData = user.volunteer
              const servePeriod = findRelevantServePeriod(volunteerData?.serveDates ?? null)
              const isSelf = isVolunteerRole && currentUserId != null && user.id === currentUserId
              const isLeader = isGroupLeaderUser(user, group)
              const shouldShowFullInfo =
                viewerSeesFullParticipantNames || isSelf || isLeader
              const canOpenUpdate = canUpdateTargetUser(user.id)
              const displayName =
                isVolunteerRole && !shouldShowFullInfo
                  ? maskHomeownerLastName(user.fullname ?? '') || 'Unknown'
                  : (user.fullname ?? user.email ?? 'Unknown')
              const isLeaderNotYetAdded = isLeader && !user.joinedAt
              return (
                <div
                  key={user.id}
                  className={cn(
                    'bg-background rounded-lg border border-border p-4 shadow-sm space-y-3',
                    isLeader && 'bg-muted/50'
                  )}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      {canManageParticipants && (
                      <Checkbox
                        checked={selectedIds.has(user.id)}
                        onCheckedChange={() => toggleSelected(user.id)}
                        aria-label="Select participant"
                        className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary shrink-0"
                      />
                      )}
                      <Avatar className="h-10 w-10 shrink-0">
                        <AvatarImage src={user.profilePicture ?? undefined} className="object-cover object-top" />
                        <AvatarFallback className="bg-muted text-sm">{shortenName(displayName)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        {!canOpenUpdate ? (
                          <span className="text-foreground font-medium text-sm">
                            {displayName}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-primary hover:underline cursor-pointer text-left font-medium text-sm"
                            onClick={() => handleNameClick(user)}
                          >
                            {displayName}
                          </button>
                        )}
                        <p className="text-muted-foreground text-xs truncate">
                          {(volunteerData?.organizationName ?? '').trim() || '—'}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {(user.role as { name?: string } | undefined)?.name ?? '—'}
                        </p>
                        {volunteerData?.srUserId != null && (
                          <p className="text-muted-foreground text-xs tabular-nums">
                            SR ID {volunteerData.srUserId}
                          </p>
                        )}
                        <p className="text-muted-foreground text-xs tabular-nums">
                          {canManageParticipants ? (
                            <>
                              Age{' '}
                              {(() => {
                                const age = getVolunteerAge(user)
                                return age != null ? age : '—'
                              })()}
                              {' '}
                              · M/F {volunteerGenderShort(user)}
                            </>
                          ) : (
                            <>
                              M/F {volunteerGenderShort(user)}
                            </>
                          )}
                        </p>
                      </div>
                    </div>
                    {canManageParticipants && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="link"
                        size="icon"
                        className="w-8 h-8 rounded-md hover:bg-muted"
                        onClick={() => openUpdateUser(user)}
                        aria-label="Update volunteer"
                      >
                        <Pencil className="w-4 h-4 text-foreground" />
                      </Button>
                      {!isLeaderNotYetAdded && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => handleDelete(user)}
                          aria-label="Remove from group"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm border-t pt-3">
                    <div>
                      <p className="text-muted-foreground text-xs">Last Active</p>
                      <p className="text-foreground">
                        {user.lastActiveAt
                          ? format(parseDateValue(user.lastActiveAt) ?? new Date(), 'dd-MMM-yyyy')
                          : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Arrival</p>
                      <p className="text-foreground">
                        {servePeriod.from ? format(servePeriod.from, 'dd-MMM-yyyy') : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <p className="text-muted-foreground text-xs">Departure</p>
                      <p className="text-foreground">
                        {servePeriod.to ? format(servePeriod.to, 'dd-MMM-yyyy') : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className="relative max-h-[min(85vh,56rem)] w-full overflow-auto print:max-h-none print:overflow-visible">
          <Table
            noScrollWrapper
            className="min-w-[3200px] print:min-w-0 print:w-full print:text-xs"
          >
            <TableHeader
              className={cn(
                'print:table-header-group',
                '[&_th]:sticky [&_th]:top-0 [&_th]:z-[25] [&_th]:shadow-[0_1px_0_0_hsl(var(--border))] [&_th]:print:!static'
              )}
            >
              <TableRow>
                {canManageParticipants && (
                <TableHead
                  className="bg-muted"
                  data-print-hide-col="true"
                  style={{
                    position: 'sticky',
                    left: 0,
                    top: 0,
                    zIndex: 40,
                    width: 40,
                    minWidth: 40,
                  }}
                >
                  <Checkbox
                    checked={filtered.length > 0 && selectedIds.size === filtered.length}
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all volunteers"
                    className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
                  />
                </TableHead>
                )}
                <TableHead
                  className={cn('bg-muted', isColumnFiltered('volunteer') && 'bg-yellow-100 print:bg-yellow-100')}
                  data-print-hide-col={!shouldPrintColumn('volunteer') ? 'true' : undefined}
                  style={{
                    position: 'sticky',
                    left: canManageParticipants ? 40 : 0,
                    top: 0,
                    zIndex: 35,
                  }}
                >
                  <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                    <span>Name</span>
                    <ColumnSortButton
                      columnKey="volunteer"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onCycleSort={handleSortColumn}
                      ariaLabelBase="Name"
                    />
                  </div>
                </TableHead>
                <TableHead
                  className={cn(
                    'bg-muted w-[5.5rem] tabular-nums',
                    isColumnFiltered('srUserId') && 'bg-yellow-100 print:bg-yellow-100'
                  )}
                  data-print-hide-col={!shouldPrintColumn('srUserId') ? 'true' : undefined}
                >
                  <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                    <span className="whitespace-nowrap">SR ID</span>
                    <ColumnSortButton
                      columnKey="srUserId"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onCycleSort={handleSortColumn}
                      ariaLabelBase="SR User ID"
                    />
                  </div>
                </TableHead>
                {canManageParticipants && (
                  <TableHead
                    className={cn('bg-muted w-[3.25rem]', isColumnFiltered('age') && 'bg-yellow-100 print:bg-yellow-100')}
                    data-print-hide-col={!shouldPrintColumn('age') ? 'true' : undefined}
                  >
                    <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                      <span>Age</span>
                      <ColumnSortButton
                        columnKey="age"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onCycleSort={handleSortColumn}
                        ariaLabelBase="Age"
                      />
                    </div>
                  </TableHead>
                )}
                <TableHead
                  className={cn('bg-muted w-[3.25rem]', isColumnFiltered('mf') && 'bg-yellow-100 print:bg-yellow-100')}
                  data-print-hide-col={!shouldPrintColumn('mf') ? 'true' : undefined}
                >
                  <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                    <span className="whitespace-nowrap">M/F</span>
                    <ColumnSortButton
                      columnKey="mf"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onCycleSort={handleSortColumn}
                      ariaLabelBase="M/F"
                    />
                  </div>
                </TableHead>
                {singleExperienceSkillFilter && (
                  <TableHead
                    className={cn('whitespace-nowrap bg-muted', isColumnFiltered('experience-skill') && 'bg-yellow-100 print:bg-yellow-100')}
                    data-print-hide-col={!shouldPrintColumn('experience-skill') ? 'true' : undefined}
                  >
                    <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                      <span className="text-center leading-tight">
                        {ALL_SKILLS_FILTER_OPTIONS.find((o) => o.value === singleExperienceSkillFilter)?.label ?? singleExperienceSkillFilter}
                      </span>
                      <ColumnSortButton
                        columnKey="experience-skill"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onCycleSort={handleSortColumn}
                        ariaLabelBase="Skill"
                      />
                    </div>
                  </TableHead>
                )}
                <TableHead
                  className={cn('bg-muted', isColumnFiltered('role') && 'bg-yellow-100 print:bg-yellow-100')}
                  data-print-hide-col={!shouldPrintColumn('role') ? 'true' : undefined}
                >
                  <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                    <span>Role</span>
                    <ColumnSortButton
                      columnKey="role"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onCycleSort={handleSortColumn}
                      ariaLabelBase="Role"
                    />
                  </div>
                </TableHead>
                {SKILL_COLUMNS.map((col) => {
                  const headingTintClass =
                    col.category === 'construction'
                      ? 'bg-sky-100 dark:bg-sky-950'
                      : col.category === 'other'
                        ? 'bg-violet-100 dark:bg-violet-950'
                        : 'bg-amber-100 dark:bg-amber-950'
                  return (
                    <TableHead
                      key={col.key}
                      className={cn('text-center', headingTintClass, col.headerClassName, isColumnFiltered(col.key) && 'bg-yellow-100 print:bg-yellow-100')}
                      data-print-hide-col={!shouldPrintColumn(col.key) ? 'true' : undefined}
                    >
                      <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                        {renderCompactHeaderLabel(col.label)}
                        <ColumnSortButton
                          columnKey={col.key}
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onCycleSort={handleSortColumn}
                          ariaLabelBase={col.label}
                        />
                      </div>
                    </TableHead>
                  )
                })}
                {canViewNotes && (
                  <TableHead
                    className={cn('bg-muted', isColumnFiltered('notes') && 'bg-yellow-100 print:bg-yellow-100')}
                    data-print-hide-col={!shouldPrintColumn('notes') ? 'true' : undefined}
                  >
                    <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                      <span>Notes</span>
                      <ColumnSortButton
                        columnKey="notes"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onCycleSort={handleSortColumn}
                        ariaLabelBase="Notes"
                      />
                    </div>
                  </TableHead>
                )}
                {showFlightDetailsColumn && (
                  <TableHead
                    className={cn('bg-muted', isColumnFiltered('flightDetails') && 'bg-yellow-100 print:bg-yellow-100')}
                    data-print-hide-col={!shouldPrintColumn('flightDetails') ? 'true' : undefined}
                  >
                    <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                      <span>Flight Details</span>
                      <ColumnSortButton
                        columnKey="flightDetails"
                        sortColumn={sortColumn}
                        sortDirection={sortDirection}
                        onCycleSort={handleSortColumn}
                        ariaLabelBase="Flight Details"
                      />
                    </div>
                  </TableHead>
                )}
                {isAdminOrBaseLeader && (
                  <>
                    <TableHead className="bg-muted text-right tabular-nums align-top">
                      <div className="flex items-center justify-end gap-1 min-w-0 flex-wrap">
                        <span>Cost</span>
                        <ColumnSortButton
                          columnKey="goal"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onCycleSort={handleSortColumn}
                          ariaLabelBase="Cost"
                        />
                      </div>
                    </TableHead>
                    <TableHead className="bg-muted text-right tabular-nums align-top">
                      <div className="flex items-center justify-end gap-1 min-w-0 flex-wrap">
                        <span>Payment</span>
                        <ColumnSortButton
                          columnKey="raised"
                          sortColumn={sortColumn}
                          sortDirection={sortDirection}
                          onCycleSort={handleSortColumn}
                          ariaLabelBase="Payment"
                        />
                      </div>
                    </TableHead>
                  </>
                )}
                <TableHead
                  className={cn('bg-muted', isColumnFiltered('lastActive') && 'bg-yellow-100 print:bg-yellow-100')}
                  data-print-hide-col={!shouldPrintColumn('lastActive') ? 'true' : undefined}
                >
                  <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                    {renderCompactHeaderLabel('Last Active')}
                    <ColumnSortButton
                      columnKey="lastActive"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onCycleSort={handleSortColumn}
                      ariaLabelBase="Last Active"
                    />
                  </div>
                </TableHead>
                <TableHead
                  className={cn('bg-muted', isColumnFiltered('added') && 'bg-yellow-100 print:bg-yellow-100')}
                  data-print-hide-col={!shouldPrintColumn('added') ? 'true' : undefined}
                >
                  <div className="flex items-center justify-center gap-1 min-w-0 flex-wrap">
                    <span>Added</span>
                    <ColumnSortButton
                      columnKey="added"
                      sortColumn={sortColumn}
                      sortDirection={sortDirection}
                      onCycleSort={handleSortColumn}
                      ariaLabelBase="Added"
                    />
                  </div>
                </TableHead>
                {canManageParticipants && <TableHead className="text-right bg-muted" data-print-hide-col="true">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedFiltered.map((row) => {
                const user = row.user
                const volunteerData = user.volunteer
                const servePeriod = findRelevantServePeriod(volunteerData?.serveDates ?? null)
                const isSelf = isVolunteerRole && currentUserId != null && user.id === currentUserId
                const isLeader = isGroupLeaderUser(user, group)
                const shouldShowFullInfo =
                  viewerSeesFullParticipantNames || isSelf || isLeader
                const canOpenUpdate = canUpdateTargetUser(user.id)
                const displayName =
                  isVolunteerRole && !shouldShowFullInfo
                    ? maskHomeownerLastName(user.fullname ?? '') || 'Unknown'
                    : (user.fullname ?? user.email ?? 'Unknown')
                const isLeaderNotYetAdded = isLeader && !user.joinedAt
                const nameSkillHighlightClass = isAdminOrBaseLeader
                  ? constructionSkillUserIds.has(user.id)
                    ? 'bg-emerald-50/70 dark:bg-emerald-950/25'
                    : otherSkillUserIds.has(user.id)
                      ? 'bg-amber-50/70 dark:bg-amber-950/25'
                      : null
                  : null
                return (
                <TableRow
                  key={user.id}
                  className={isLeader ? 'bg-muted' : undefined}
                  data-state={selectedIds.has(user.id) ? 'selected' : undefined}
                >
                  {canManageParticipants && (
                  <TableCell
                    className={cn(isLeader ? 'bg-muted' : 'bg-background')}
                    data-print-hide-col="true"
                    style={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 20,
                      width: 40,
                      minWidth: 40,
                    }}
                  >
                    <Checkbox
                      checked={selectedIds.has(user.id)}
                      onCheckedChange={() => toggleSelected(user.id)}
                      aria-label="Select row"
                      className="rounded-md border-muted-foreground/70 data-[state=checked]:border-primary"
                    />
                  </TableCell>
                  )}
                  <TableCell
                    className={cn(
                      nameSkillHighlightClass ?? (isLeader ? 'bg-muted' : 'bg-background'),
                      isColumnFiltered('volunteer') && 'bg-yellow-50/70 print:bg-yellow-50'
                    )}
                    data-print-hide-col={!shouldPrintColumn('volunteer') ? 'true' : undefined}
                    style={{
                      position: 'sticky',
                      left: canManageParticipants ? 40 : 0,
                      zIndex: 10,
                    }}
                  >
                    <div className="flex items-center gap-2">
                      <Avatar className="print:hidden">
                        <AvatarImage src={user.profilePicture ?? undefined} className="object-cover object-top" />
                        <AvatarFallback className="bg-muted text-sm">{shortenName(displayName)}</AvatarFallback>
                      </Avatar>
                      {!canOpenUpdate ? (
                        <span className="text-foreground font-medium">
                          {displayName}
                        </span>
                      ) : (
                      <button
                        type="button"
                        className="text-primary hover:underline cursor-pointer text-left font-medium"
                        onClick={() => handleNameClick(user)}
                      >
                        {displayName}
                      </button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell
                    className={cn(
                      'text-muted-foreground text-center tabular-nums',
                      isColumnFiltered('srUserId') && 'bg-yellow-50/70 print:bg-yellow-50'
                    )}
                    data-print-hide-col={!shouldPrintColumn('srUserId') ? 'true' : undefined}
                  >
                    {volunteerData?.srUserId != null ? volunteerData.srUserId : '—'}
                  </TableCell>
                  {canManageParticipants && (
                    <TableCell
                      className={cn('text-muted-foreground text-center tabular-nums', isColumnFiltered('age') && 'bg-yellow-50/70 print:bg-yellow-50')}
                      data-print-hide-col={!shouldPrintColumn('age') ? 'true' : undefined}
                    >
                      {(() => {
                        const age = getVolunteerAge(user)
                        return age != null ? age : '—'
                      })()}
                    </TableCell>
                  )}
                  <TableCell
                    className={cn(
                      'text-muted-foreground font-medium text-center tabular-nums',
                      isColumnFiltered('mf') && 'bg-yellow-50/70 print:bg-yellow-50'
                    )}
                    data-print-hide-col={!shouldPrintColumn('mf') ? 'true' : undefined}
                  >
                    {volunteerGenderShort(user)}
                  </TableCell>
                  {singleExperienceSkillFilter && (
                    <TableCell
                      className={cn('text-muted-foreground font-medium tabular-nums', isColumnFiltered('experience-skill') && 'bg-yellow-50/70 print:bg-yellow-50')}
                      data-print-hide-col={!shouldPrintColumn('experience-skill') ? 'true' : undefined}
                    >
                      {getExperienceRating(user, singleExperienceSkillFilter) ?? '—'}
                    </TableCell>
                  )}
                  <TableCell
                    className={cn('text-muted-foreground font-medium', isColumnFiltered('role') && 'bg-yellow-50/70 print:bg-yellow-50')}
                    data-print-hide-col={!shouldPrintColumn('role') ? 'true' : undefined}
                  >
                    {(user.role as { name?: string } | undefined)?.name ?? '—'}
                  </TableCell>
                  {SKILL_COLUMNS.map((col) => {
                    const rating = getSkillColumnRating(user, col)
                    return (
                      <TableCell
                        key={`${user.id}-${col.key}`}
                        className={cn('text-muted-foreground tabular-nums text-center', col.cellClassName, isColumnFiltered(col.key) && 'bg-yellow-50/70 print:bg-yellow-50')}
                        data-print-hide-col={!shouldPrintColumn(col.key) ? 'true' : undefined}
                      >
                        {rating ?? '—'}
                      </TableCell>
                    )
                  })}
                  {canViewNotes && (
                    <TableCell
                      className={cn('text-muted-foreground max-w-[260px]', isColumnFiltered('notes') && 'bg-yellow-50/70 print:bg-yellow-50')}
                      data-print-hide-col={!shouldPrintColumn('notes') ? 'true' : undefined}
                    >
                      {(() => {
                        const note = getVolunteerNotes(user)
                        if (!note) return '—'
                        const isExpanded = expandedNotesIds.has(user.id)
                        return (
                          <button
                            type="button"
                            className="text-left w-full hover:underline"
                            onClick={() => openUpdateUser(user)}
                            title={note}
                          >
                            <span className={cn(!isExpanded && 'line-clamp-1')}>
                              {note}
                            </span>
                          </button>
                        )
                      })()}
                    </TableCell>
                  )}
                  {showFlightDetailsColumn && (
                    <TableCell
                      className={cn('text-muted-foreground max-w-[300px] whitespace-pre-line', isColumnFiltered('flightDetails') && 'bg-yellow-50/70 print:bg-yellow-50')}
                      data-print-hide-col={!shouldPrintColumn('flightDetails') ? 'true' : undefined}
                    >
                      {(() => {
                        const flightDetails = getVolunteerFlightDetails(user)
                        if (!flightDetails) return '—'
                        return (
                          <button
                            type="button"
                            className="text-left w-full hover:underline"
                            onClick={() => openUpdateUser(user)}
                            title={flightDetails}
                          >
                            <span className="line-clamp-2">
                              {flightDetails}
                            </span>
                          </button>
                        )
                      })()}
                    </TableCell>
                  )}
                  {isAdminOrBaseLeader && (
                    <>
                      <TableCell className="text-muted-foreground text-right tabular-nums">
                        {formatSrMoney(user.groupPrice)}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-right tabular-nums">
                        {formatSrMoney(user.groupPaid)}
                      </TableCell>
                    </>
                  )}
                  <TableCell
                    className={cn('text-muted-foreground', isColumnFiltered('lastActive') && 'bg-yellow-50/70 print:bg-yellow-50')}
                    data-print-hide-col={!shouldPrintColumn('lastActive') ? 'true' : undefined}
                  >
                    {user.lastActiveAt
                      ? format(parseDateValue(user.lastActiveAt) ?? new Date(), 'dd-MMM-yyyy')
                      : 'N/A'}
                  </TableCell>
                  <TableCell
                    className={cn('text-muted-foreground', isColumnFiltered('added') && 'bg-yellow-50/70 print:bg-yellow-50')}
                    data-print-hide-col={!shouldPrintColumn('added') ? 'true' : undefined}
                  >
                    {user.joinedAt ? format(new Date(user.joinedAt), 'dd-MMM-yyyy') : '—'}
                  </TableCell>
                  {canManageParticipants && (
                  <TableCell className="text-right" data-print-hide-col="true">
                    <div className="flex justify-end items-center gap-2">
                      <Button
                        variant="link"
                        size="icon"
                        className="w-7 h-7 bg-foreground rounded-md hover:bg-foreground/90"
                        onClick={() => openUpdateUser(user)}
                        aria-label="Update volunteer"
                      >
                        <Pencil className="w-4 text-white" />
                      </Button>
                      {!isLeaderNotYetAdded && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDelete(user)}
                        aria-label="Remove from group"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                      )}
                    </div>
                  </TableCell>
                  )}
                </TableRow>
              )})}
            </TableBody>
          </Table>
          </div>
        )}
      </div>

      <FormDialog
        isOpen={!!updateUserTargetId}
        setIsOpen={(open) => !open && setUpdateUserTargetId(null)}
        title="Update Volunteer"
        description="Update Volunteer information."
        trigger={<span className="hidden" aria-hidden />}
      >
        {fetchUserLoading && <p className="text-muted-foreground">Loading…</p>}
        {fetchUserError && <p className="text-destructive">{fetchUserError}</p>}
        {fetchedUser && !fetchUserLoading && (
          <UpdateUserForm
            user={fetchedUser}
            onSuccessCallback={() => setUpdateUserTargetId(null)}
          />
        )}
      </FormDialog>
    </div>
  )
}
